import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import {
  v2ContainerSizeAtom,
  v2ZoomAtom,
  v2ZoomFitModeAtom,
} from '../../store/v2/viewer.atom';
import {
  V2_FIT_WIDTH_HINT_MIN_ZOOM,
  clampV2Zoom,
} from '../../components/v2/zoom.constants';

interface Props {
  /** 현재 페이지 기준 fit-width 배율(useFitZoom 결과) */
  fitWidthZoom: number;
}

/**
 * 세로로 긴 페이지에서 가로 맞춤 전환을 권유하는 힌트 칩의 노출/해제 상태.
 * 뷰어는 자동으로 배율을 바꾸지 않고, 사용자가 칩을 눌렀을 때만 fit-width를 적용한다.
 *
 * 해제 조건(한 번 해제하면 뷰어가 언마운트될 때까지 다시 뜨지 않는다.
 * 페이지를 넘겨도 재노출하지 않으며 localStorage에는 남기지 않는다):
 * - 칩 클릭(가로 맞춤 적용) / X 버튼
 * - 사용자의 직접 줌 조작 → fitMode가 'fitPage'를 벗어나는 순간.
 *   자동 줌을 제거한 뒤로 'fitPage' 이탈 경로는 모두 사용자 조작뿐이다
 *   (툴바 ±·프리셋·드롭다운, 모바일 ±, 휠/핀치/더블탭 → useAnchoredZoom).
 *   리사이즈는 zoom만 바꾸고 fitMode는 유지하므로 오탐이 없다.
 */
const useFitWidthHint = ({ fitWidthZoom }: Props) => {
  const containerSize = useAtomValue(v2ContainerSizeAtom);
  const [fitMode, setFitMode] = useAtom(v2ZoomFitModeAtom);
  const setZoom = useSetAtom(v2ZoomAtom);

  /*
   * 드롭다운에서 '페이지 맞춤'을 다시 고르면 fitMode가 fitPage로 복귀한다.
   * 파생값만 쓰면 칩이 되살아나므로 해제 상태를 따로 고정한다.
   */
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (fitMode !== 'fitPage') {
      setIsDismissed(true);
    }
  }, [fitMode]);

  const dismissHint = useCallback(() => setIsDismissed(true), []);

  /* ZoomControls의 applyFitWidth와 동일 경로 */
  const applyFitWidth = useCallback(() => {
    setZoom(clampV2Zoom(fitWidthZoom));
    setFitMode('fitWidth');
    setIsDismissed(true);
  }, [fitWidthZoom, setZoom, setFitMode]);

  /* 컨테이너 측정 전에는 fitWidthZoom이 1로 고정돼 깜빡임이 생긴다 */
  const isMeasured = containerSize.width > 0 && containerSize.height > 0;
  const isLongPage = fitWidthZoom >= V2_FIT_WIDTH_HINT_MIN_ZOOM;

  return {
    isHintVisible:
      isMeasured && !isDismissed && fitMode === 'fitPage' && isLongPage,
    applyFitWidth,
    dismissHint,
  };
};

export default useFitWidthHint;
